import { MutationFunction } from '@apollo/client';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

import { INGREDIENT_GENERATION_API } from '../../app/constants/constants';
import { CreatedIngredient, Recipe, RecipeVariationStep, StepSubStepsMutation, StepSubstepUpdateMutation, Substep, SubstepIngredient, SubStepIngredientsMutation, UpdateRecipeInterface } from "../../app/recipeCreation/createRecipe.types";
import { CREATED_SOURCE, SubStepTypeEnum } from '../../components/recipeCreation/utils/constants';
import cookingTimeCalculation from "../../utils/cookingTimeUtil";

export const getSelectedRecipeVariation = (recipe: Recipe, recipeVariationId: string) => recipe.Recipe_Variations.find((variation) => variation.id === recipeVariationId) ?? null;

export const getSelectedRecipeVariationSteps = (recipe: Recipe, recipeVariationId: string) => recipe.Recipe_Variations.find((variation) => variation.id === recipeVariationId)?.RecipeVariations_Steps ?? [];

export const getSelectedStep = (recipeVariationSteps: RecipeVariationStep[], stepId: string) => recipeVariationSteps?.find((recipeVarStep) => recipeVarStep.step_id === stepId) ?? null;

export const getUpdatedCookingTime = (recipeVariationSteps: RecipeVariationStep[], newSubstep: Substep) => {
  const instructionSubsteps: Substep[] = [newSubstep];

  recipeVariationSteps.forEach((recipeVarStep) => {
    recipeVarStep.StepsE3.Steps_Substeps.forEach((substep) => {
      if (substep.SubstepsE3.type === "instruction") {
        instructionSubsteps.push(substep.SubstepsE3);
      }
    })
  })
  return Math.ceil(cookingTimeCalculation(instructionSubsteps) / 60);
}

export const getUpdatedStepSubSteps = (step: RecipeVariationStep | null, addSubStepAt: number, stepId: string, newSubstepId: string) => {
  const updatedStepSubsteps: StepSubstepUpdateMutation[] = [];
  const insertStepSubstep: StepSubStepsMutation[] = [];

  if (step) {

    const stepSubsteps = step.StepsE3.Steps_Substeps ?? [];

    stepSubsteps.forEach((stepSubstep, index) => {
      const { SubstepsE3: excludedSubsteps, __typename: stepSubstepTypeNameExluded, ...stepSubstepWithoutSubstep } = stepSubstep;

      if (index < addSubStepAt) {
        updatedStepSubsteps.push({
          where: { id: { _eq: stepSubstepWithoutSubstep.id } },
          _set: { ...stepSubstepWithoutSubstep, subStep_rank: index }
        })
        if (index === stepSubsteps.length - 1) {
          insertStepSubstep.push({
            id: uuid(),
            step_id: stepId,
            subStep_id: newSubstepId,
            subStep_rank: index + 1
          })
        }
      } else if (index === addSubStepAt) {

        insertStepSubstep.push({
          id: uuid(),
          step_id: stepId,
          subStep_id: newSubstepId,
          subStep_rank: index
        })

        updatedStepSubsteps.push({
          where: { id: { _eq: stepSubstepWithoutSubstep.id } },
          _set: { ...stepSubstepWithoutSubstep, subStep_rank: index + 1 }
        })

      } else {

        updatedStepSubsteps.push({
          where: { id: { _eq: stepSubstepWithoutSubstep.id } },
          _set: { ...stepSubstepWithoutSubstep, subStep_rank: index + 1 }
        })

      }
    })

  }

  return {
    updatedStepSubsteps,
    insertStepSubstep
  }
};

export const initializeStepData = (stepId: string, recipeVariationId: string, newSubstepId: string) => {
  const stepDataInsert = {
    id: stepId,
    name: "",
    description: "",
    step_purpose: "",
  }

  const recipeVarStepsInsert = {
    recipeVariation_id: recipeVariationId,
    step_id: stepId,
    id: uuid(),
    step_rank: 0,
  }

  const stepSubstepInsert = {
    id: uuid(),
    step_id: stepId,
    subStep_id: newSubstepId,
    subStep_rank: 0,
  }

  return {
    stepDataInsert,
    recipeVarStepsInsert,
    stepSubstepInsert
  }
}

export const removeIngredientUpdatedIngredientIdList = (oldRecipe: Recipe, recipeVariationId: string, subStepUId: string) => {

  const updatedIngredientsList: string[] = [];
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);

  selectedRecipeVariation?.RecipeVariations_Steps?.forEach((stepSubstep) => {
    stepSubstep.StepsE3.Steps_Substeps.forEach((substep) => {
      if (substep.id !== subStepUId) {
        substep.SubstepsE3.SubSteps_Ingredients_E3s.forEach((substepIngredient) => {
          updatedIngredientsList.push(substepIngredient.ingredient_id)
        })
      }
    })
  })

  return Array.from(new Set(updatedIngredientsList));

}

export const chooseSaveTypeChange = ({
  oldSubstepType,
  newSubstepType,
  isOldSubstepPreset,
  isNewSubstepPreset,
  addSubstep,
  initializeStep
}: {
  oldSubstepType?: string,
  newSubstepType: string,
  isOldSubstepPreset: boolean,
  isNewSubstepPreset: boolean,
  addSubstep: boolean,
  initializeStep: boolean
}) => {
  if (initializeStep) {
    if (isNewSubstepPreset) return "initializePreset";
    if (newSubstepType === SubStepTypeEnum.INSTRUCTION) return "initializeInstruction";
    if (newSubstepType === SubStepTypeEnum.INGREDIENTS) return "initializeIngredient";
    if (newSubstepType === SubStepTypeEnum.OUTSIDE_INSTRUCTION) return "initializeOutsideInstruction";
  }

  if (addSubstep) {
    if (isNewSubstepPreset) return "addPreset";
    if (newSubstepType === SubStepTypeEnum.INSTRUCTION) return "addInstruction";
    if (newSubstepType === SubStepTypeEnum.INGREDIENTS) return "addIngredient";
    if (newSubstepType === SubStepTypeEnum.OUTSIDE_INSTRUCTION) return "addOutsideInstruction";
  }

  if (isOldSubstepPreset) {
    if (isNewSubstepPreset) return "presetTOpreset";
    return `presetTO${newSubstepType}`;
  }

  if (isNewSubstepPreset) return `${oldSubstepType}TOpreset`;

  return `${oldSubstepType}TO${newSubstepType}`;
};

export const getAddedandRemovedIngredients = (oldSubstepIngredients: SubstepIngredient[], newSubstepIngredients: SubstepIngredient[]) => {
  const added: SubStepIngredientsMutation[] = [];
  const deleted: string[] = [];

  newSubstepIngredients.forEach(updatedObj => {
    if (!oldSubstepIngredients.some(originalObj => originalObj.ingredient_id === updatedObj.ingredient_id)) added.push(updatedObj);
  });

  oldSubstepIngredients.forEach(originalObj => {
    if (!newSubstepIngredients.some(updatedObj => updatedObj.ingredient_id === originalObj.ingredient_id)) deleted.push(originalObj.id);
  });

  return { added, deleted };
}

export const removeAndAddIngredientUpdatedIngredientIdList = (oldRecipe: Recipe, recipeVariationId: string, subStepUId: string, newSubstep: Substep) => {

  const updatedIngredientsList: string[] = [];
  const selectedRecipeVariation = getSelectedRecipeVariation(oldRecipe, recipeVariationId);

  selectedRecipeVariation?.RecipeVariations_Steps?.forEach((stepSubstep) => {
    stepSubstep.StepsE3.Steps_Substeps.forEach((substep) => {
      if (substep.id !== subStepUId) {
        substep.SubstepsE3.SubSteps_Ingredients_E3s.forEach((substepIngredient) => {
          updatedIngredientsList.push(substepIngredient.ingredient_id)
        })
      } else {
        newSubstep.SubSteps_Ingredients_E3s.forEach((substepIngredient) => {
          updatedIngredientsList.push(substepIngredient.ingredient_id)
        })
      }
    })
  })

  return Array.from(new Set(updatedIngredientsList));

}

export const generateIngredientImages = (ingredients: CreatedIngredient[]) => {
  const newIngredinetsList = ingredients.map((ingredient) => ({
    ingredientName: ingredient.displayName,
    ingredientID: ingredient.id
  }))

  axios.post(INGREDIENT_GENERATION_API, {
    message: newIngredinetsList,
    updateIngredient: true
  }).then((response) => { console.log(response.data) }).catch((error) => { console.error(error) })

}

export const ingredientInsertsVals = (newIngredientsList: CreatedIngredient[]) => newIngredientsList.map((ingredient: CreatedIngredient) => ({
  _id: ingredient.id,
  created_by: ingredient.created_by,
  displayName: ingredient.displayName,
  conversion: [{ "unit": "", "factor": "" }],
  preps: [{ "loop": true, "mute": true, "prepId": "", "duration": "", "howMedia": "", "startTime": "", "displayName": "", "postPrepImage": "", "postPrepDescription": "" }],
  created_source: CREATED_SOURCE.USER_CREATED
}))

export const updateRecipeLastUpdatedAtAndTags = async ({
  oldRecipe,
  updateLastUpdatedAtAndTags,
}: {
  oldRecipe: Recipe;
  updateLastUpdatedAtAndTags: MutationFunction;
}) => {
  const variables: UpdateRecipeInterface = {
    id: oldRecipe.id,
    recipeUpdatedAtAndResetData: {
      last_edited_at: new Date().toUTCString(),
    },
  };

  if (oldRecipe?.cuisine?.length > 0) {
    variables.recipeUpdatedAtAndResetData.cuisine = [];
  }
  if (oldRecipe?.meal_type?.length > 0) {
    variables.recipeUpdatedAtAndResetData.meal_type = [];
  }
  if (oldRecipe?.diet?.length > 0) {
    variables.recipeUpdatedAtAndResetData.diet = [];
  }

  await updateLastUpdatedAtAndTags({
    variables,
  });
};
